import { SelectProps } from "@cofano/cck/Select/Select";
import { Select } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { ContractStatus } from "__generated__/enums";
import client from "../../apollo";
import { NextContractStatusesDocument, NextContractStatusesQuery } from "__generated__/view/contractsNew/graphql/contract.queries.generated";
import { ChildPropsFromArray } from "types/commons";
import { useEffect } from "react";

export type ContractStatusSelectOption = ChildPropsFromArray<NextContractStatusesQuery, "nextContractStatuses">;

enum ContractStatusGroup {
	Group = "GROUP",
	Active = "ACTIVE",
	Finished = "FINISHED"
}

const GroupOrder = {
	[ContractStatusGroup.Group]: 1,
	[ContractStatusGroup.Active]: 2,
	[ContractStatusGroup.Finished]: 3,
} as const;

const StatusGroupMap = {
	[ContractStatus.Draft]: ContractStatusGroup.Group,
	[ContractStatus.Quoted]: ContractStatusGroup.Group,
	[ContractStatus.InNegotiation]: ContractStatusGroup.Group,
	[ContractStatus.Rejected]: ContractStatusGroup.Group,

	[ContractStatus.Accepted]: ContractStatusGroup.Active,
	[ContractStatus.Active]: ContractStatusGroup.Active,

	[ContractStatus.Finished]: ContractStatusGroup.Finished,
	[ContractStatus.Inactive]: ContractStatusGroup.Finished,
} as const;

const ContractStatusSelect = <Multiple extends boolean = false,>({
	currentStatus,
	onInitLoad,
	...rest
}: Omit<SelectProps<ContractStatusSelectOption, Multiple>, "fetchOptions" | "paginated"> & {
	currentStatus?: ContractStatus | null,
	onInitLoad?: (statuses: ContractStatusSelectOption[]) => void,
}) => {

	const onFetchOptions = async () => {
		try  {
			const result = await client.query({
				query: NextContractStatusesDocument,
				variables: {
					currentStatus: currentStatus,
				},
			});

			return result.data.nextContractStatuses
				.toSorted((a, b) => {
					const groupA = StatusGroupMap[a.name];
					const groupB = StatusGroupMap[b.name];
					return GroupOrder[groupA] - GroupOrder[groupB];
				});
		} catch (response) {
			return [];
		}
	};

	useEffect(() => {
		let ignore = false;
		if (onInitLoad) {
			onFetchOptions().then(statuses => {
				if (!ignore) {
					onInitLoad(statuses);
				}
			});
		}

		return () => {
			ignore = true;
		};
	}, []);

	return (
		<Select
			fetchOptions={onFetchOptions}
			groupBy={(option) => intl.formatMessage({ id: StatusGroupMap[option.name] })}
			getOptionLabel={(option) => intl.formatMessage({ id: `${option.languageKeyPrefix}${option.name}` })}
			getOptionColor={(option) => option.color}
			disableSearch
			{...rest}
		/>
	);
};

export default ContractStatusSelect;
